// ============================================================================
// CloseCaseModal — case-level close action
// ============================================================================
// Reason dropdown is filtered server-side by case_type_scope + caller
// permissions. Picking a reason populates the WIN/LOSE/NEUTRAL outcome label;
// remarks are mandatory; recovered_amount is optional (shows only for
// FAVOURABLE reasons by convention but kept editable for flexibility).

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import {
  useClosureReasonOptions,
  useCloseCase,
} from 'hooks/api/useCaseClosure';
import type { OutcomeCategory } from 'api/types/caseClosure.types';

export interface CloseCaseModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  onClose: () => void;
  onClosed: () => void;
}

const OUTCOME_LABEL: Record<OutcomeCategory, string> = {
  FAVOURABLE: 'Win',
  UNFAVOURABLE: 'Lose',
  NEUTRAL: 'Neutral',
};

const OUTCOME_COLOR: Record<OutcomeCategory, { bg: string; fg: string }> = {
  FAVOURABLE: { bg: '#dcfce7', fg: '#166534' },
  UNFAVOURABLE: { bg: '#fee2e2', fg: '#991b1b' },
  NEUTRAL: { bg: 'var(--bg-secondary)', fg: 'var(--text-secondary)' },
};

const CloseCaseModal: React.FC<CloseCaseModalProps> = ({
  isOpen, caseId, caseCode, onClose, onClosed,
}) => {
  const { data: reasons, loading: reasonsLoading } = useClosureReasonOptions(isOpen ? caseId : null);
  const { close, loading: saving } = useCloseCase();

  const [reasonId, setReasonId] = useState<string>('');
  const [remarks, setRemarks] = useState<string>('');
  const [recoveredAmount, setRecoveredAmount] = useState<string>('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [confirmOpen, setConfirmOpen] = useState(false);

  // Reset on open
  useEffect(() => {
    if (!isOpen) return;
    setReasonId('');
    setRemarks('');
    setRecoveredAmount('');
    setFieldErrors({});
  }, [isOpen]);

  const selectedReason = useMemo(
    () => reasons.find((r) => r.id === reasonId),
    [reasons, reasonId],
  );

  const reasonOptions = useMemo(
    () => reasons.map((r) => ({ value: r.id, label: `${r.closureName} — ${OUTCOME_LABEL[r.outcomeCategory]}` })),
    [reasons],
  );

  const handleClose = useCallback(() => {
    if (saving) return;
    onClose();
  }, [saving, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!reasonId) errors.reasonId = 'Select a closure reason';
    if (!remarks.trim()) errors.remarks = 'Remarks are required';
    if (recoveredAmount && !Number.isFinite(Number(recoveredAmount))) {
      errors.recoveredAmount = 'Must be a number';
    }
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setConfirmOpen(true);
  }, [reasonId, remarks, recoveredAmount]);

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    try {
      await close(caseId, {
        closureReasonId: reasonId,
        closureRemarks: remarks.trim(),
        recoveredAmount: recoveredAmount ? Number(recoveredAmount) : null,
      });
      notificationService.toast.success(`Case ${caseCode} closed.`);
      onClosed();
      onClose();
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Failed to close case.',
      );
    }
  }, [close, caseId, caseCode, reasonId, remarks, recoveredAmount, onClosed, onClose]);

  if (!isOpen) return null;

  const outcome = selectedReason?.outcomeCategory;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Close Case — {caseCode}</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="reasonId">
            <SyutSelect
              label="Closure Reason"
              required
              options={reasonOptions}
              value={reasonId}
              onChange={(v) => setReasonId((v as string) ?? '')}
              error={fieldErrors.reasonId}
              isLoading={reasonsLoading}
              placeholder={reasonsLoading ? 'Loading…' : 'Select reason'}
            />
          </div>

          {outcome && (
            <div style={{ marginBottom: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>Outcome:</span>
              <span style={{
                padding: '2px 10px', borderRadius: '999px',
                fontSize: '0.72rem', fontWeight: 600,
                background: OUTCOME_COLOR[outcome].bg,
                color: OUTCOME_COLOR[outcome].fg,
              }}>
                {OUTCOME_LABEL[outcome]}
              </span>
            </div>
          )}

          <div className="form-group" data-field="remarks">
            <label className="form-label">Remarks <span className="text-danger">*</span></label>
            <textarea
              className={`form-textarea${fieldErrors.remarks ? ' form-field-error' : ''}`}
              rows={4}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Why is the case being closed? Reference recovery transaction if applicable."
            />
            {fieldErrors.remarks && <div className="form-error-message">{fieldErrors.remarks}</div>}
          </div>

          <div className="form-group" data-field="recoveredAmount">
            <label className="form-label">
              Recovered Amount
              <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: '0.4rem' }}>
                (optional, ₹)
              </span>
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              className={`form-input${fieldErrors.recoveredAmount ? ' form-field-error' : ''}`}
              value={recoveredAmount}
              onChange={(e) => setRecoveredAmount(e.target.value)}
              placeholder="0.00"
            />
            {fieldErrors.recoveredAmount && (
              <div className="form-error-message">{fieldErrors.recoveredAmount}</div>
            )}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Closing…' : 'Close Case'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Close"
        message={`Close case ${caseCode}? The workflow will be marked completed and further transitions will be blocked until the case is reopened.`}
        confirmLabel="Close Case"
        cancelLabel="Cancel"
        variant="danger"
        loading={saving}
        loadingMessage="Closing…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default CloseCaseModal;
